import { useMemo, useState, type FormEvent } from "react";
import { AdminDataTable } from "@/components/admin/AdminDataTable";
import { AdminFormDrawer } from "@/components/admin/AdminFormDrawer";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { EmptyState } from "@/components/admin/EmptyState";
import { MediaPickerModal } from "@/components/media/MediaPickerModal";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { SafeImage } from "@/components/ui/SafeImage";
import { useCurrentUser } from "@/features/auth/hooks";
import { roleAtLeast } from "@/features/auth/roles";
import { useCreateReference, useDeleteReference, useReferences, useUpdateReference } from "@/features/references/hooks";
import type { ReferenceEntry, ReferenceInput } from "@/features/references/types";

const emptyForm: ReferenceInput = {
  name: "",
  organization: "",
  roleTitle: "",
  quote: "",
  projectType: "",
  rating: null,
  avatarKey: null,
  featured: false,
  url: "",
  note: "",
  sortOrder: 0,
  isPublished: true,
};

export function ReferencesPage() {
  const currentUser = useCurrentUser();
  const references = useReferences();
  const createReference = useCreateReference();
  const updateReference = useUpdateReference();
  const deleteReference = useDeleteReference();
  const [drawerMode, setDrawerMode] = useState<"create" | "edit" | null>(null);
  const [selectedReference, setSelectedReference] = useState<ReferenceEntry | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ReferenceEntry | null>(null);
  const [form, setForm] = useState<ReferenceInput>(emptyForm);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canDelete = roleAtLeast(currentUser.data?.role, "ADMIN");

  const sortedReferences = useMemo(
    () => [...(references.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [references.data],
  );

  function openCreate() {
    setSelectedReference(null);
    setForm(emptyForm);
    setError(null);
    setDrawerMode("create");
  }

  function openEdit(reference: ReferenceEntry) {
    setSelectedReference(reference);
    setForm({
      name: reference.name,
      organization: reference.organization ?? "",
      roleTitle: reference.roleTitle ?? "",
      quote: reference.quote,
      projectType: reference.projectType ?? "",
      rating: reference.rating,
      avatarKey: reference.avatarKey,
      featured: reference.featured,
      url: reference.url ?? "",
      note: reference.note ?? "",
      sortOrder: reference.sortOrder,
      isPublished: reference.isPublished,
    });
    setError(null);
    setDrawerMode("edit");
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedReference(null);
    setForm(emptyForm);
    setError(null);
  }

  function isValidOptionalUrl(value: string | null | undefined) {
    if (!value) return true;
    try {
      const url = new URL(value);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch {
      return false;
    }
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError("Customer name is required.");
      return;
    }
    if (!form.quote.trim()) {
      setError("Quote / testimonial text is required.");
      return;
    }
    if (form.rating != null && (form.rating < 1 || form.rating > 5)) {
      setError("Rating must be between 1 and 5.");
      return;
    }
    if (!isValidOptionalUrl(form.url)) {
      setError("URL must be a valid http or https URL.");
      return;
    }

    if (drawerMode === "create") {
      createReference.mutate(
        { input: form, avatarFile: null },
        {
          onSuccess: closeDrawer,
          onError: (err) => setError(err instanceof Error ? err.message : "Unable to create reference."),
        },
      );
      return;
    }

    if (selectedReference) {
      updateReference.mutate(
        { reference: selectedReference, input: form, avatarFile: null },
        {
          onSuccess: closeDrawer,
          onError: (err) => setError(err instanceof Error ? err.message : "Unable to update reference."),
        },
      );
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteReference.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="References / Testimonials"
        description="Manage customer quotes, ratings, featured references, and public visibility."
        actions={<Button onClick={openCreate}>New Reference</Button>}
      />
      <AdminDataTable
        rows={sortedReferences}
        getRowKey={(reference) => reference.id}
        emptyState={
          <EmptyState
            title={references.isLoading ? "Loading references..." : "No references yet"}
            description="Create the first customer reference or testimonial."
            action={!references.isLoading && <Button onClick={openCreate}>Create Reference</Button>}
          />
        }
        columns={[
          {
            key: "avatar",
            header: "Image",
            render: (reference) =>
              reference.avatarUrl ? (
                <SafeImage src={reference.avatarUrl} alt={reference.name} fallbackLabel={reference.name.slice(0, 2)} className="size-14 rounded-md object-cover" />
              ) : (
                <div className="grid size-14 place-items-center rounded-md border border-cyan-300/15 bg-black/40 text-xs text-content-muted">None</div>
              ),
          },
          {
            key: "customer",
            header: "Customer",
            render: (reference) => (
              <div>
                <p className="font-medium text-white">{reference.name}</p>
                <p className="mt-1 text-xs text-content-muted">{[reference.roleTitle, reference.organization].filter(Boolean).join(" / ") || "No title"}</p>
              </div>
            ),
          },
          {
            key: "quote",
            header: "Quote",
            render: (reference) => <p className="max-w-md truncate">{reference.quote}</p>,
          },
          {
            key: "rating",
            header: "Rating",
            render: (reference) => (reference.rating ? `${reference.rating}/5` : <span className="text-content-muted">None</span>),
          },
          { key: "sortOrder", header: "Sort", render: (reference) => reference.sortOrder },
          {
            key: "status",
            header: "Status",
            render: (reference) => (
              <div className="flex flex-wrap gap-2">
                <StatusBadge tone={reference.isPublished ? "active" : "inactive"}>{reference.isPublished ? "Active" : "Inactive"}</StatusBadge>
                {reference.featured && <StatusBadge tone="warning">Featured</StatusBadge>}
              </div>
            ),
          },
          {
            key: "actions",
            header: "Actions",
            className: "px-4 py-4 align-middle text-right",
            render: (reference) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => openEdit(reference)}>
                  Edit
                </Button>
                {canDelete && reference.deletedAt === null && (
                  <Button size="sm" variant="danger" onClick={() => setDeleteTarget(reference)}>
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      <AdminFormDrawer
        isOpen={drawerMode !== null}
        title={drawerMode === "create" ? "New Reference" : "Edit Reference"}
        description="Choose an avatar image from the centralized Media Library."
        onClose={closeDrawer}
      >
        <form className="space-y-5" onSubmit={handleSubmit}>
          <TextField label="Customer Name" value={form.name} onChange={(value) => setForm((current) => ({ ...current, name: value }))} required />
          <TextField label="Customer Title" value={form.roleTitle ?? ""} onChange={(value) => setForm((current) => ({ ...current, roleTitle: value }))} />
          <TextField label="Organization Name" value={form.organization ?? ""} onChange={(value) => setForm((current) => ({ ...current, organization: value }))} />
          <label className="block">
            <span className="text-sm text-content-secondary">Quote / Testimonial</span>
            <textarea
              className="mt-2 min-h-32 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-2 text-sm text-white outline-none transition focus:border-cyan-300"
              value={form.quote}
              onChange={(event) => setForm((current) => ({ ...current, quote: event.target.value }))}
              required
              maxLength={2000}
            />
          </label>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Project Type" value={form.projectType ?? ""} onChange={(value) => setForm((current) => ({ ...current, projectType: value }))} />
            <TextField
              label="Rating"
              value={form.rating == null ? "" : String(form.rating)}
              type="number"
              min={1}
              max={5}
              onChange={(value) => setForm((current) => ({ ...current, rating: value ? Number(value) : null }))}
            />
          </div>
          <TextField label="URL" value={form.url ?? ""} type="url" onChange={(value) => setForm((current) => ({ ...current, url: value }))} />
          <TextField
            label="Sort Order"
            value={String(form.sortOrder ?? 0)}
            type="number"
            onChange={(value) => setForm((current) => ({ ...current, sortOrder: Number(value) }))}
          />
          <div className="rounded-md border border-cyan-300/15 bg-black/20 p-3">
            <p className="text-sm text-content-secondary">Image / Avatar</p>
            {form.avatarKey ? <p className="mt-2 truncate text-xs text-cyan-100">{form.avatarKey}</p> : <p className="mt-2 text-xs text-content-muted">No media selected</p>}
            <div className="mt-3 flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setMediaPickerOpen(true)}>Choose Media</Button>
              {form.avatarKey && <Button type="button" variant="ghost" onClick={() => setForm((current) => ({ ...current, avatarKey: null }))}>Clear</Button>}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <ToggleField label="Active" checked={Boolean(form.isPublished)} onChange={(value) => setForm((current) => ({ ...current, isPublished: value }))} />
            <ToggleField label="Featured" checked={Boolean(form.featured)} onChange={(value) => setForm((current) => ({ ...current, featured: value }))} />
          </div>
          {!canDelete && <p className="text-xs text-content-muted">Only Admin, Owner, and Developer roles can delete references.</p>}
          {error && <p className="rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button type="submit" disabled={createReference.isPending || updateReference.isPending}>
              {createReference.isPending || updateReference.isPending ? "Saving..." : "Save Reference"}
            </Button>
          </div>
        </form>
      </AdminFormDrawer>

      <MediaPickerModal
        isOpen={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={(items) => setForm((current) => ({ ...current, avatarKey: items[0]?.filename ?? current.avatarKey }))}
      />

      <ConfirmDeleteDialog
        isOpen={deleteTarget !== null}
        title="Delete Reference"
        description={`Soft delete "${deleteTarget?.name ?? "this reference"}"?`}
        isPending={deleteReference.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  required,
  min,
  max,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "url" | "number";
  required?: boolean;
  min?: number;
  max?: number;
}) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input
        className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
        min={min}
        max={max}
      />
    </label>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="flex items-center gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3">
      <input className="size-4 accent-cyan-300" type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="text-sm text-content-secondary">{label}</span>
    </label>
  );
}
